import { UrlContext } from '../state/url-context'
import { base64decode, base64encode } from './b64-encoding'

export const DEFAULT_URL_CONTEXT = { sitemap: 'WebMainArea' }

/**
 * Parser for query string params, this follows closely the implementation
 * of the browser UrlSearchParams the only difference is that this will not try to
 * decode special characters but will leave the value as it is. This is usefull
 * for b64 string that should not be decoded to preveserve their original value.
 * So with this for example values that contain + signs will still have the + sign
 * when parsed, while with the browser UrlSearchParam that + sign would be decoded into a space
 * breaking the base 64 decoding.
 */
export class UrlParams {
  private readonly qs: string
  private readonly params: Record<string, string>

  constructor(search: string) {
    this.qs = search.substring(1)
    this.params = {}
    this.parseQueryString()
  }

  public get(key: string) {
    return this.params[key]
  }

  private parseQueryString() {
    this.qs.split('&').reduce((a, b) => {
      const index = b.indexOf('=')
      const key = b.substring(0, index)
      const val = b.substring(index + 1)
      a[key] = decodeURIComponent(val)
      return a
    }, this.params)
  }
}

export function serialize(context: UrlContext): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cloned: any = { ...context }
  return base64encode(JSON.stringify(cloned))
}

/**
 * Deserialize a URL string into a `UrlContext` object.
 *
 * @param {string} str - The URL string to deserialize.
 * @returns {UrlContext} The deserialized URL context object.
 */
export const deserializeUrl = (str: string): UrlContext => {
  const url = new URL(`${window.origin}${str}`)
  const params = new UrlParams(url.search)
  const context = params.get('s')

  let result = { ...DEFAULT_URL_CONTEXT }
  if (context) {
    const json = base64decode(context)
    const parsed = JSON.parse(json)
    result = { ...parsed }
  }

  /**
   * The sitemap is now part of the url, so I'm adding it back from the url to the context object.
   */
  const splitted = url.pathname.split('/')
  if (splitted.length >= 3 && splitted[2] != '') {
    result.sitemap = splitted[2]
  }

  return result
}

/**
 *
 * @param {string} original_string
 * @param {string} param_name
 * @param {string} param_value
 * @returns {string} given an url string, adds the given query param to it and returns the new string
 * ensuring the correct usage of ? or & depending if other query params are present or not
 */
export const addQueryParams = (original_string: string, param_name: string, param_value: string): string => {
  if (!original_string) {
    return original_string
  }
  if (original_string.includes('?')) {
    return `${original_string}&${param_name}=${param_value}`
  } else {
    return `${original_string}?${param_name}=${param_value}`
  }
}
